import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { Menu, Phone } from "lucide-react";
import LeadDialog from "@/components/LeadDialog";
import { COMPANY } from "@/data/content";

const NAV = [
  { label: "CPE", href: "/#cpe" },
  { label: "Décret Tertiaire", href: "/#decret" },
  { label: "CEE", href: "/#cee" },
  { label: "Travaux", href: "/#travaux" },
  { label: "Simulateur", href: "/#simulateur" },
  { label: "FAQ", href: "/#faq" },
  { label: "Blog", href: "/blog" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200 transition-shadow duration-300 ${
        scrolled ? "shadow-sm" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-24 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <span className="h-16 w-16 bg-white rounded-md p-1 flex items-center justify-center shadow-md">
            <img src="/logo.jpg" alt="Logo PRIMEC2E CEE" className="h-full w-full object-contain" />
          </span>
          <span className="font-display font-black text-2xl tracking-tight text-midnight">
            {COMPANY.name}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) =>
            n.href.startsWith("/#") ? (
              <a key={n.label} href={n.href} className="text-sm font-medium link-underline transition-colors text-slate-600 hover:text-midnight">
                {n.label}
              </a>
            ) : (
              <Link key={n.label} to={n.href} className="text-sm font-medium link-underline transition-colors text-slate-600 hover:text-midnight">
                {n.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-2 text-sm font-medium transition-colors text-midnight" data-testid="header-phone">
            <Phone className="h-4 w-4 text-emerald" /> {COMPANY.phone}
          </a>
          <LeadDialog leadType="audit">
            <Button data-testid="header-cta" className="rounded-none bg-emerald hover:bg-emerald-600 text-white font-medium px-6">
              Demander un audit
            </Button>
          </LeadDialog>
        </div>

        {/* Mobile */}
        <div className="lg:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" className="text-midnight">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <SheetTitle className="sr-only">Menu de navigation</SheetTitle>
              <div className="flex flex-col gap-1 mt-10">
                {NAV.map((n) => (
                  <SheetClose asChild key={n.label}>
                    {n.href.startsWith("/#") ? (
                      <a href={n.href} className="py-3 text-lg font-medium text-midnight border-b border-slate-100">{n.label}</a>
                    ) : (
                      <Link to={n.href} className="py-3 text-lg font-medium text-midnight border-b border-slate-100">{n.label}</Link>
                    )}
                  </SheetClose>
                ))}
                <div className="mt-6">
                  <LeadDialog leadType="audit">
                    <Button className="w-full rounded-none bg-emerald hover:bg-emerald-600 text-white">Demander un audit</Button>
                  </LeadDialog>
                </div>
                <a href={`tel:${COMPANY.phoneRaw}`} className="mt-4 flex items-center gap-2 text-midnight font-medium">
                  <Phone className="h-4 w-4 text-emerald" /> {COMPANY.phone}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
